import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './downloads-indicator.module.css';

import { useIsMusyncConfigured, useMusyncPermissions } from '/@/renderer/api/musync/musync-client';
import {
    MusyncDownloadStage,
    MusyncFeishinDownloadBatch,
} from '/@/renderer/api/musync/musync-types';
import {
    useCleanFeishinDownloads,
    useFeishinDownloads,
} from '/@/renderer/features/downloads/api/downloads-api';
import {
    FEISHIN_DOWNLOADS_ROUTE,
    isBatchActive,
    isBatchStuck,
    summarizeDownloadsIndicator,
} from '/@/renderer/features/downloads/downloads-flow';
import { Badge } from '/@/shared/components/badge/badge';
import { Button } from '/@/shared/components/button/button';
import { Group } from '/@/shared/components/group/group';
import { Icon } from '/@/shared/components/icon/icon';
import { Popover } from '/@/shared/components/popover/popover';
import { Progress } from '/@/shared/components/progress/progress';
import { Stack } from '/@/shared/components/stack/stack';
import { Text } from '/@/shared/components/text/text';

const STAGE_COLORS: Record<MusyncDownloadStage, string> = {
    cancelled: 'gray',
    complete: 'green',
    downloading: 'blue',
    failed: 'red',
    healing: 'cyan',
    importing: 'yellow',
    partial: 'orange',
    unknown: 'gray',
};

const RecentDownloadRow = ({ batch, now }: { batch: MusyncFeishinDownloadBatch; now: number }) => {
    const { t } = useTranslation();
    const title = batch.catalog.album || batch.name;
    const isMoving = isBatchActive(batch, now);
    const isStuck = isBatchStuck(batch, now);
    const stageLabel = isStuck
        ? t('page.downloads.stalled', { defaultValue: 'Stalled' })
        : t(`page.downloads.stage.${batch.stage}`, { defaultValue: batch.stage });

    return (
        <Stack className={styles.row} gap={4}>
            <Group gap="sm" justify="space-between" wrap="nowrap">
                <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text fw={600} lineClamp={1} size="sm" title={title}>
                        {title}
                    </Text>
                    <Text isMuted lineClamp={1} size="xs">
                        {[batch.catalog.artist, batch.owner].filter(Boolean).join(' · ')}
                    </Text>
                </Stack>
                <Badge
                    color={isStuck ? 'orange' : STAGE_COLORS[batch.stage] || 'gray'}
                    size="sm"
                    variant="light"
                >
                    {isMoving ? `${batch.percent}%` : stageLabel}
                </Badge>
            </Group>
            {isMoving && (
                <Progress
                    aria-label={stageLabel}
                    color={batch.stage === 'importing' ? 'yellow' : 'primary'}
                    size="xs"
                    value={batch.stage === 'importing' ? 100 : batch.percent}
                />
            )}
        </Stack>
    );
};

/**
 * Compact "what is downloading right now" affordance for the action bar: a
 * badge with the number of batches that are really moving, a red dot for
 * anything that needs attention (failed, partial or stalled), and a popover
 * with the newest few, a clean shortcut and a link to the full Downloads page.
 * Renders nothing without musync.
 */
export const DownloadsIndicator = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [opened, setOpened] = useState(false);
    const configured = useIsMusyncConfigured();
    // Feishin downloads need musync's `download` capability; a read-only role
    // would only collect 403s here.
    const { canDownload } = useMusyncPermissions();
    const downloads = useFeishinDownloads(configured && canDownload);
    const clean = useCleanFeishinDownloads();
    // The activity window is judged against the moment the data arrived, not
    // a render-time clock: every poll bumps dataUpdatedAt even when the payload
    // is structurally identical, so a zombie batch ages out without a timer.
    const now = downloads.dataUpdatedAt;
    const summary = useMemo(
        () => summarizeDownloadsIndicator(downloads.data?.items || [], 5, now),
        [downloads.data?.items, now],
    );

    if (!configured || !canDownload) return null;

    const hasActivity = summary.activeCount > 0;
    const needsAttention = summary.attentionCount > 0;
    const label = hasActivity
        ? t('page.downloads.indicatorActive', {
              count: summary.activeCount,
              defaultValue: `${summary.activeCount} downloads in progress`,
          })
        : needsAttention
          ? t('page.downloads.indicatorAttention', {
                count: summary.attentionCount,
                defaultValue: `${summary.attentionCount} downloads need attention`,
            })
          : t('page.downloads.title', { defaultValue: 'Downloads' });

    return (
        <Popover onChange={setOpened} opened={opened} position="bottom-start" width={320}>
            <Popover.Target>
                <Button
                    aria-label={label}
                    className={styles.trigger}
                    onClick={() => setOpened((current) => !current)}
                    p="0"
                    title={label}
                >
                    <Icon
                        className={hasActivity ? styles.breathing : undefined}
                        icon="download"
                        size="lg"
                    />
                    {hasActivity && (
                        <span className={styles.count} data-failed={needsAttention}>
                            {summary.activeCount}
                        </span>
                    )}
                    {!hasActivity && needsAttention && <span className={styles.dot} data-failed />}
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="sm">
                    <Group justify="space-between" wrap="nowrap">
                        <Text fw={600}>
                            {t('page.downloads.title', { defaultValue: 'Downloads' })}
                        </Text>
                        <Group gap={4} wrap="nowrap">
                            <Button
                                disabled={clean.isPending || summary.cleanableCount === 0}
                                onClick={() => clean.mutate()}
                                size="compact-xs"
                                title={t('page.downloads.cleanHint', {
                                    defaultValue: 'Hide finished downloads from this list',
                                })}
                                variant="subtle"
                            >
                                {t('page.downloads.clean', { defaultValue: 'Clean' })}
                            </Button>
                            <Button
                                onClick={() => {
                                    setOpened(false);
                                    navigate(FEISHIN_DOWNLOADS_ROUTE);
                                }}
                                rightSection={<Icon icon="arrowRightS" size="sm" />}
                                size="compact-xs"
                                variant="subtle"
                            >
                                {t('page.downloads.viewAll', { defaultValue: 'View all' })}
                            </Button>
                        </Group>
                    </Group>
                    {summary.recent.length === 0 ? (
                        <Text isMuted size="sm">
                            {t('page.downloads.indicatorEmpty', {
                                defaultValue: 'No recent downloads.',
                            })}
                        </Text>
                    ) : (
                        <Stack gap={0}>
                            {summary.recent.map((batch) => (
                                <RecentDownloadRow batch={batch} key={batch.batch_id} now={now} />
                            ))}
                        </Stack>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};
